import * as Sentry from '@sentry/react';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import type { RealtimeChannel } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useTenant } from '@/hooks/useTenant';
import { onInvalidApiKey } from '@/hooks/useVersionCheck';
import { RealtimeContext, type RealtimeStatus } from './realtimeContext';

export { useRealtime } from './realtimeContext';

type RealtimeMode = 'broadcast' | 'postgres' | 'off';

// Per-tab override (localStorage 'realtime-mode') lets one browser try
// broadcast in production before the env default is flipped.
const MODE: RealtimeMode = (() => {
  let override: string | null = null;
  try {
    override = localStorage.getItem('realtime-mode');
  } catch {
    // storage unavailable (private mode, blocked) — use the env default
  }
  const raw = String(override ?? import.meta.env.VITE_REALTIME_MODE ?? 'broadcast').trim();
  return raw === 'postgres' || raw === 'off' ? raw : 'broadcast';
})();

const BATCH_MS = 250;
const ERRORS_BEFORE_BACKOFF = 5;

// Tables present in the supabase_realtime publication (postgres mode only).
const POSTGRES_TABLES = ['leads', 'jobs', 'job_payments', 'job_parts', 'customers', 'email_messages'] as const;

type QueryKeyPrefix = readonly unknown[];

// List-level query prefixes to refresh when a row in the table changes.
const TABLE_KEYS: Record<string, readonly QueryKeyPrefix[]> = {
  leads: [['leads'], ['csr-leads'], ['leads-dashboard'], ['dashboard-stats'], ['daily-huddle'], ['customer-metrics'], ['comparison-metrics'], ['metro-health'], ['reports']],
  jobs: [['jobs'], ['leads-dashboard'], ['dashboard-stats'], ['daily-huddle'], ['technician-performance'], ['customer-metrics'], ['comparison-metrics'], ['metro-health'], ['projects'], ['project'], ['reports']],
  job_payments: [['jobs'], ['invoices'], ['invoice-stats'], ['dashboard-stats'], ['reports']],
  job_parts: [['jobs'], ['reports']],
  customers: [['customers'], ['customer-metrics'], ['duplicate-customers'], ['caller-lookup']],
  sms_messages: [['omni-channel-threads'], ['unread-messages-count'], ['sms-threads'], ['thread-messages'], ['sms-messages'], ['communications'], ['communications-count'], ['customer-activity']],
  email_messages: [['omni-channel-threads'], ['unread-messages-count'], ['thread-messages'], ['communications'], ['communications-count'], ['customer-activity']],
  call_logs: [['call-logs'], ['communications'], ['communications-count'], ['omni-channel-threads'], ['customer-activity'], ['caller-lookup']],
  customer_communications: [['communications'], ['communications-count'], ['customer-activity'], ['notifications']],
  lead_activities: [['customer-activity']],
  companies: [['tenant'], ['fleet']],
  profiles: [['tenant', 'me'], ['users'], ['csr-profiles'], ['profile']],
  user_roles: [['tenant', 'me'], ['users'], ['csr-profiles']],
  user_permissions: [['tenant', 'me'], ['users']],
  service_types: [['service-types']],
  techs: [['techs'], ['techs-with-metros'], ['fleet']],
  metros: [['metros'], ['metro-health']],
  lead_sources: [['lead-sources'], ['lead-source-with-campaigns'], ['campaigns-with-costs']],
  campaigns: [['campaigns'], ['lead-source-with-campaigns'], ['campaigns-with-costs'], ['lead-campaign-check']],
  parts_catalog: [['parts-catalog']],
  service_catalog: [['service-catalog']],
  estimates: [['estimates'], ['estimate-stats'], ['estimates-expiring-soon'], ['job-audit-log']],
  invoices: [['invoices'], ['invoice-stats']],
  job_photos: [['job-photos']],
};

// Detail query prefix per table; narrowed to [prefix, id] when the row id is known.
const DETAIL_KEY: Record<string, string> = {
  leads: 'lead',
  jobs: 'job',
  customers: 'customer',
  call_logs: 'call-log',
  estimates: 'estimate',
  invoices: 'invoice',
  projects: 'project',
};

const CUSTOMER_COMMS_TABLES = new Set(['sms_messages', 'email_messages', 'call_logs', 'customer_communications']);

interface ChangeEvent {
  table: string;
  op?: string;
  id?: string;
  customer_id?: string;
  lead_id?: string;
  job_id?: string;
}

function keysFor(change: ChangeEvent): QueryKeyPrefix[] {
  const keys: QueryKeyPrefix[] = [...(TABLE_KEYS[change.table] ?? [])];

  const detail = DETAIL_KEY[change.table];
  if (detail) keys.push(change.id ? [detail, change.id] : [detail]);

  if (CUSTOMER_COMMS_TABLES.has(change.table)) {
    keys.push(change.customer_id
      ? ['customer-unified-communications', change.customer_id]
      : ['customer-unified-communications']);
  }

  if (change.customer_id) {
    keys.push(
      ['customer', change.customer_id],
      ['customer-leads', change.customer_id],
      ['customer-jobs', change.customer_id],
      ['customer-communications', change.customer_id],
      ['customer-estimates', change.customer_id],
      ['customer-invoices', change.customer_id],
    );
  } else if (change.table === 'leads' || change.table === 'jobs') {
    keys.push(['customer-leads'], ['customer-jobs']);
  }

  if (change.lead_id) {
    keys.push(['lead', change.lead_id], ['lead-activities', change.lead_id], ['lead-estimates', change.lead_id], ['lead-campaign-check', change.lead_id]);
  } else if (change.table === 'lead_activities' || change.table === 'estimates') {
    keys.push(['lead-activities'], ['lead-estimates']);
  }

  if (change.job_id) {
    keys.push(['job', change.job_id], ['job-payments', change.job_id], ['job-parts', change.job_id], ['job-invoice', change.job_id], ['job-photos', change.job_id], ['job-audit-log', change.job_id]);
  } else if (change.table === 'job_payments' || change.table === 'job_parts' || change.table === 'invoices') {
    keys.push(['job'], ['job-payments'], ['job-parts'], ['job-invoice']);
  }

  // audit_logs itself is not broadcast (20260908120000): the tech RPCs always update the
  // jobs row in the same transaction, so a jobs change for this id refreshes its timeline.
  if (change.table === 'jobs') {
    keys.push(change.id ? ['job-audit-log', change.id] : ['job-audit-log']);
  }

  return keys;
}

/** Coalesces a burst of row changes into one invalidation pass per query key. */
class InvalidationBatcher {
  private pending = new Map<string, QueryKeyPrefix>();
  private timer: ReturnType<typeof setTimeout> | null = null;

  constructor(private readonly queryClient: QueryClient) {}

  add(change: ChangeEvent) {
    for (const key of keysFor(change)) {
      this.pending.set(JSON.stringify(key), key);
    }
    if (!this.timer) {
      this.timer = setTimeout(() => this.flush(), BATCH_MS);
    }
  }

  flush() {
    this.timer = null;
    const keys = [...this.pending.values()];
    this.pending.clear();
    for (const queryKey of keys) {
      void this.queryClient.invalidateQueries({ queryKey });
    }
  }

  dispose() {
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    this.pending.clear();
  }
}

function asId(value: unknown): string | undefined {
  return typeof value === 'string' || typeof value === 'number' ? String(value) : undefined;
}

// Broadcast-from-database: one message per table, `event` = table name,
// payload {table, op, id, customer_id?, lead_id?, job_id?}. Also accepts the
// generic `event: 'change'` form so either trigger shape works.
function fromBroadcast(message: { event?: string; payload?: unknown }): ChangeEvent | null {
  const payload = (message.payload ?? {}) as Record<string, unknown>;
  const table = asId(payload.table) ?? (message.event && message.event !== 'change' ? message.event : undefined);
  if (!table) return null;
  return {
    table,
    op: asId(payload.op),
    id: asId(payload.id),
    customer_id: asId(payload.customer_id),
    lead_id: asId(payload.lead_id),
    job_id: asId(payload.job_id),
  };
}

function fromPostgres(payload: { table: string; eventType: string; new: unknown; old: unknown }): ChangeEvent {
  const row = ((payload.eventType === 'DELETE' ? payload.old : payload.new) ?? {}) as Record<string, unknown>;
  return {
    table: payload.table,
    op: payload.eventType,
    id: asId(row.id),
    customer_id: asId(row.customer_id),
    lead_id: asId(row.lead_id),
    job_id: asId(row.job_id),
  };
}

function isAuthFailure(text: string): boolean {
  return /\b401\b|invalid api key|unauthori[sz]ed/i.test(text);
}

export function RealtimeProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const { tenantId, userId } = useTenant();
  const [status, setStatus] = useState<RealtimeStatus>(MODE === 'off' ? 'off' : 'idle');

  useEffect(() => {
    if (MODE === 'off') return;
    if (!tenantId || !userId) {
      setStatus('idle');
      return;
    }

    const topic = `tenant:${tenantId}`;
    const batcher = new InvalidationBatcher(queryClient);
    let channel: RealtimeChannel | null = null;
    let disposed = false;
    let errorCount = 0;
    let everSubscribed = false;
    let sentryWarned = false;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;

    const handle = (change: ChangeEvent | null) => {
      if (change && !disposed) batcher.add(change);
    };

    const build = (): RealtimeChannel => {
      const ch = supabase.channel(topic, MODE === 'broadcast' ? { config: { private: true } } : undefined);
      if (MODE === 'broadcast') {
        ch.on('broadcast', { event: '*' }, (message) => handle(fromBroadcast(message)));
        return ch;
      }
      for (const table of POSTGRES_TABLES) {
        ch.on(
          'postgres_changes',
          { event: '*', schema: 'public', table, filter: `company_id=eq.${tenantId}` },
          (payload) => handle(fromPostgres(payload)),
        );
      }
      return ch;
    };

    const onError = async (state: string, err: Error | undefined) => {
      errorCount += 1;
      setStatus('error');
      const text = err?.message ?? String(err ?? state);

      if (isAuthFailure(text)) onInvalidApiKey();

      if (!sentryWarned) {
        sentryWarned = true;
        Sentry.captureMessage(`Realtime channel ${state}`, {
          level: 'warning',
          tags: { realtimeMode: MODE, realtimeState: state },
          extra: { topic, error: text },
        });
      }

      // A hidden tab can wake with an expired JWT on the socket; push the
      // current one before the channel's own rejoin fires.
      const { data: { session } } = await supabase.auth.getSession();
      if (!disposed && session?.access_token) {
        await supabase.realtime.setAuth(session.access_token);
      }

      if (!disposed && errorCount >= ERRORS_BEFORE_BACKOFF) {
        const delay = Math.min(1000 * 2 ** (errorCount - ERRORS_BEFORE_BACKOFF), 60_000) + Math.random() * 1000;
        if (channel) {
          void supabase.removeChannel(channel);
          channel = null;
        }
        retryTimer = setTimeout(() => void subscribe(), delay);
      }
    };

    const subscribe = async () => {
      if (disposed) return;
      setStatus('connecting');

      // supabase.channel() returns an existing channel with the same topic;
      // a previous instance still leaving would be reused mid-teardown.
      const stale = supabase.getChannels().find((c) => c.topic === `realtime:${topic}`);
      if (stale) await supabase.removeChannel(stale);
      if (disposed) return;

      channel = build();
      channel.subscribe((state, err) => {
        if (disposed) return;
        switch (state) {
          case 'SUBSCRIBED':
            errorCount = 0;
            setStatus('connected');
            if (everSubscribed) {
              void queryClient.invalidateQueries({ refetchType: 'active' });
            }
            everSubscribed = true;
            break;
          case 'CLOSED':
            setStatus('disconnected');
            break;
          case 'CHANNEL_ERROR':
          case 'TIMED_OUT':
            void onError(state, err);
            break;
          default:
            break;
        }
      });
    };

    void subscribe();

    return () => {
      disposed = true;
      if (retryTimer) clearTimeout(retryTimer);
      batcher.dispose();
      if (channel) void supabase.removeChannel(channel);
      channel = null;
    };
  }, [tenantId, userId, queryClient]);

  const value = useMemo(() => ({ status, isConnected: status === 'connected' }), [status]);

  return (
    <RealtimeContext.Provider value={value}>
      {children}
    </RealtimeContext.Provider>
  );
}
